// Focus refine-flow diagnostics.
//
// The reported bug — "the question sometimes just switches, not on user input;
// it's gone, replaced by another step" — is invisible after the fact: by the
// time anyone looks, the readiness card has been re-emitted a dozen times and
// the timeline tells you nothing about WHICH card was on screen WHEN, or why
// the step changed.
//
// This records every step-lifecycle moment into a capped ring buffer on
// `window.__loomFocusDiag` (and mirrors notable ones to `console.info` with a
// `[focus-diag]` tag). Crucially it captures, on every readiness card, BOTH the
// pinned current question AND what the OLD "first non-green field" derivation
// WOULD have shown — so a `would_have_switched` entry is the smoking gun that
// proves the agent re-carded under the user without any input of theirs.
//
// Dump the whole timeline from a Playwright driver or the console with:
//
//   JSON.stringify(window.__loomFocusDiag, null, 2)   // or dumpFocusDiag()
//
// Mirrors apps/web/src/lib/ws-diag.ts. Keep entries cheap and structured; this
// is a diagnostic surface, not a product feature.

export interface FocusDiagEntry {
  /** Wall-clock ISO timestamp. */
  readonly t: string;
  /** Milliseconds since the buffer was first written, for gap analysis. */
  readonly dt: number;
  readonly kind: string;
  readonly detail?: Record<string, unknown>;
}

const MAX_ENTRIES = 400;

interface DiagHost {
  __loomFocusDiag?: FocusDiagEntry[];
  __loomFocusDiagStart?: number;
  /** Manual override toggle for the on-screen panel (window.__loomFocusDebug = true). */
  __loomFocusDebug?: boolean;
  /** Console helpers (attached at import). */
  dumpFocusDiag?: () => string;
  fieldHistory?: (field: string) => unknown;
}

const host = (): DiagHost | null =>
  typeof window === "undefined" ? null : (window as unknown as DiagHost);

export const focusDiag = (
  kind: string,
  detail?: Record<string, unknown>,
  /** Routine, high-frequency entries go to the ring buffer only — never the
   *  console — so the console stays readable. */
  quiet = false,
): void => {
  const h = host();
  const now = Date.now();
  if (h !== null) {
    if (!Array.isArray(h.__loomFocusDiag)) {
      h.__loomFocusDiag = [];
      h.__loomFocusDiagStart = now;
    }
    const start = h.__loomFocusDiagStart ?? now;
    const buf = h.__loomFocusDiag;
    buf.push({
      t: new Date(now).toISOString(),
      dt: now - start,
      kind,
      ...(detail !== undefined ? { detail } : {}),
    });
    if (buf.length > MAX_ENTRIES) buf.splice(0, buf.length - MAX_ENTRIES);
  }
  if (!quiet) {
    console.info(`[focus-diag] ${kind}`, detail ?? "");
  }
};

/** Convenience for a driver/console: the whole timeline as one string. */
export const dumpFocusDiag = (): string => {
  const h = host();
  return JSON.stringify(h?.__loomFocusDiag ?? [], null, 2);
};

/** The last `n` entries (most recent last). Used by the on-screen panel. */
export const recentFocusDiag = (n = 12): ReadonlyArray<FocusDiagEntry> => {
  const h = host();
  const buf = h?.__loomFocusDiag ?? [];
  return buf.slice(Math.max(0, buf.length - n));
};

/**
 * Verdict evolution of ONE field across every readiness card observed — the
 * tool for diagnosing the milestone/priority re-ask loop. Each `card` diag
 * entry carries a `v` array of `{f,v,r?,val?,s?}` (field, verdict, reason,
 * value, source). This pulls one field's row out of every card in order:
 *
 *   fieldHistory("milestone")
 *   // [{dt, rev, verdict:"green", value:"Sprint 12"},
 *   //  {dt, rev, verdict:"needs_input", reason:"Which milestone…"},  ← re-ask!
 *   //  {dt, rev, verdict:"green", value:"Sprint 12"}, …]
 *
 * A row flipping green → needs_input/red with no user action between is the
 * loop; the `verdict`+`reason` say WHICH vector (needs_input re-ask vs red
 * grounding drift).
 */
export const fieldHistory = (
  field: string,
): ReadonlyArray<Record<string, unknown>> => {
  const h = host();
  const buf = h?.__loomFocusDiag ?? [];
  const rows: Array<Record<string, unknown>> = [];
  for (const e of buf) {
    if (e.kind !== "card") continue;
    const v = (e.detail?.v ?? []) as ReadonlyArray<Record<string, unknown>>;
    const row = Array.isArray(v) ? v.find((x) => x?.f === field) : undefined;
    if (!row) continue;
    rows.push({
      dt: Math.round(e.dt / 1000),
      rev: e.detail?.rev,
      verdict: row.v,
      ...(row.r !== undefined ? { reason: row.r } : {}),
      ...(row.val !== undefined ? { value: row.val } : {}),
      ...(row.s !== undefined ? { source: row.s } : {}),
    });
  }
  return rows;
};

/**
 * Whether the on-screen debug panel should render. Enabled by any of:
 *   • `?focusdebug` (or `?focusdebug=1`) in the URL,
 *   • `localStorage.loomFocusDebug === "1"`,
 *   • `window.__loomFocusDebug === true` (toggle live from the console).
 * Off by default so it never ships in front of a real user.
 */
export const isFocusDebugEnabled = (): boolean => {
  if (typeof window === "undefined") return false;
  const h = host();
  if (h?.__loomFocusDebug === true) return true;
  try {
    if (window.localStorage.getItem("loomFocusDebug") === "1") return true;
  } catch {
    /* storage blocked (private mode / sandboxed iframe) — fall through */
  }
  return new URLSearchParams(window.location.search).has("focusdebug");
};

// Expose the analyzers on `window` so they're callable straight from the
// console: `dumpFocusDiag()` and `fieldHistory("milestone")`.
{
  const h = host();
  if (h !== null) {
    h.dumpFocusDiag = dumpFocusDiag;
    h.fieldHistory = fieldHistory;
  }
}
